// src/components/HomeReviewsSection.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { getPublicProductReviews } from "../services/publicReviews.api";

function HomeReviewsSection() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const TEXT = {
    EN: {
      title: "Customer Reviews",
      subtitle: "Loved by ESIELL customers.",
      verifiedBuyer: "Verified buyer",
      viewAll: "View all reviews",
      viewProduct: "View product",
      loading: "Loading reviews...",
    },
    AR: {
      title: "مراجعات العملاء",
      subtitle: "آراء حقيقية من عملاء ESIELL.",
      verifiedBuyer: "عميل موثّق",
      viewAll: "عرض كل المراجعات",
      viewProduct: "عرض المنتج",
      loading: "جاري تحميل المراجعات...",
    },
  }[lang];

  useEffect(() => {
    let mounted = true;

    getPublicProductReviews()
      .then((data) => {
        if (!mounted) return;
        setReviews(Array.isArray(data.reviews) ? data.reviews.slice(0, 6) : []);
      })
      .catch(() => {
        if (mounted) setReviews([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

const renderReviewMedia = (review) => {
  if (!review.reviewMediaUrl || !review.reviewMediaType) return null;

  if (review.reviewMediaType === "image") {
    return (
      <div className="mt-3">
        <img
          src={review.reviewMediaUrl}
          alt="Customer review"
          className="h-40 w-full object-contain rounded-xl border border-[#E6D8D2] bg-white"
        />
      </div>
    );
  }

  if (review.reviewMediaType === "video") {
    return (
      <div className="mt-3">
        <video
          controls
          playsInline
          preload="metadata"
          className="h-40 w-full rounded-xl border border-[#E6D8D2] bg-black object-contain"
        >
          <source
            src={review.reviewMediaUrl}
            type={review.reviewMediaMimeType || "video/mp4"}
          />
        </video>
      </div>
    );
  }

  if (review.reviewMediaType === "audio") {
    return (
      <div className="mt-3 rounded-xl border border-[#E6D8D2] bg-white p-3">
        <p className="text-[11px] text-[#7A7674] mb-2">
          {lang === "EN" ? "Voice review" : "مراجعة صوتية"}
        </p>

        <audio controls className="w-full">
          <source
            src={review.reviewMediaUrl}
            type={review.reviewMediaMimeType || "audio/ogg"}
          />
        </audio>
      </div>
    );
  }

  return null;
};

  const renderStars = (rating) => {
    const safeRating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

    return (
      <span className="text-yellow-500 tracking-[1px]">
        {"★".repeat(safeRating)}
        <span className="text-[#D8CDC8]">{"★".repeat(5 - safeRating)}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <section className="w-full px-4 py-8 bg-white">
        <div className="max-w-6xl mx-auto text-center text-sm text-[#7A7674]">
          {TEXT.loading}
        </div>
      </section>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <section
      className="w-full px-4 py-10 bg-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#2E3135]">
              {TEXT.title}
            </h2>
            <p className="text-sm text-[#7A7674] mt-1">
              {TEXT.subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/customer-reviews")}
            className="hidden sm:inline-flex px-4 py-2 rounded-lg bg-black text-white text-sm font-semibold hover:bg-[#222] transition"
          >
            {TEXT.viewAll}
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="min-w-[280px] max-w-[280px] bg-[#FFF9F6] border border-[#E6D8D2] rounded-2xl p-4 shadow-sm"
            >
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/product/${review.seoSlug}`)}
                  className="w-16 h-16 rounded-xl border border-[#E6D8D2] overflow-hidden bg-white flex-shrink-0"
                >
                  <img
                    src={review.productImage}
                    alt={review.productNameEn}
                    className="w-full h-full object-cover"
                  />
                </button>

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => navigate(`/product/${review.seoSlug}`)}
                    className={`text-sm font-semibold text-[#2E3135] line-clamp-2 hover:underline ${
                      lang === "AR" ? "text-right" : "text-left"
                    }`}
                  >
                    {lang === "AR"
                      ? review.productNameAr || review.productNameEn
                      : review.productNameEn || review.productNameAr}
                  </button>

                  <div className="mt-1 text-sm">
                    {renderStars(review.rating)}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-semibold">
                  {(review.customerName || "C").trim().charAt(0).toUpperCase()}
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2E3135] truncate">
                    {review.customerName}
                  </p>
                  <p className="text-[11px] text-green-700 font-medium">
                    ✓ {TEXT.verifiedBuyer}
                  </p>
                </div>
              </div>

{review.reviewText && (
  <p className="mt-3 text-sm leading-6 text-[#5c5150] line-clamp-3">
    {review.reviewText}
  </p>
)}

{renderReviewMedia(review)}

<button
  type="button"
  onClick={() => navigate(`/product/${review.seoSlug}`)}
                className="mt-4 w-full py-2.5 bg-black text-white rounded-lg text-sm font-semibold hover:bg-[#222] transition"
              >
                {TEXT.viewProduct}
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => navigate("/customer-reviews")}
            className="min-w-[280px] max-w-[280px] rounded-2xl border border-[#E6D8D2] bg-black text-white p-5 flex flex-col items-center justify-center text-center shadow-sm active:scale-[0.98] transition"
          >
            <div className="text-3xl mb-3">★</div>
            <div className="text-lg font-bold">{TEXT.viewAll}</div>
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate("/customer-reviews")}
          className="sm:hidden mt-4 w-full py-3 rounded-lg bg-black text-white text-sm font-semibold"
        >
          {TEXT.viewAll}
        </button>
      </div>
    </section>
  );
}

export default HomeReviewsSection;
